import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { Alert } from "../components/alert/Alert.jsx";

export const AlertContext = createContext(null);
export const AlertDispatchContext = createContext(null);

const ALERT_TIMEOUT = 3000;

const useAlert = () => {
	const [alert, setAlert] = useState(undefined);

	const timeout = useRef(null);

	const clearAlertTimeout = useCallback(() => {
		if (!timeout.current) {
			return;
		}

		clearTimeout(timeout.current);
		timeout.current = null;
	}, []);

	useEffect(() => {
		// Cleanup
		return () => {
			clearAlertTimeout();
		};
	}, [clearAlertTimeout]);

	const addAlert = useCallback(
		(alert) => {
			clearAlertTimeout();

			setAlert(alert);

			timeout.current = setTimeout(() => {
				removeAlert();
			}, ALERT_TIMEOUT);
		},
		[clearAlertTimeout]
	);

	const removeAlert = useCallback(() => {
		clearAlertTimeout();
		setAlert(undefined);
	}, []);

	return {
		alert,
		addAlert,
		removeAlert,
	};
};

export const useAlertDispatch = () => {
	const alertDispatch = useContext(AlertDispatchContext);

	if (alertDispatch === null) {
		console.error("[Interactive-Products] AlertDispatchContext is not provided");
		return null;
	}

	return alertDispatch;
};

export const AlertProvider = ({ children }) => {
	const { alert, addAlert, removeAlert } = useAlert();

	const alertControls = useMemo(
		() => ({ addAlert, removeAlert }),
		[addAlert, removeAlert]
	);

	return (
		<AlertContext.Provider value={alert}>
			<AlertDispatchContext.Provider value={alertControls}>
				{children}

				{alert ? (
					<Alert
						alertMessage={alert.message}
						alertType={alert.type}
						onCloseButtonClick={removeAlert}
					/>
				) : null}
			</AlertDispatchContext.Provider>
		</AlertContext.Provider>
	);
};
